import type { EnumLiteralCreateOperation } from '@festina/life-general-types/life'
import { assertNotEmpty, type Optional } from '@festina-life/core'
import { IconButton } from '@festina-life/ui/actions'
import { Alert } from '@festina-life/ui/feedback'
import { RawCheckbox, RawDoubleInput } from '@festina-life/ui/forms'
import { useTranslate } from '@festina-life/ui/hooks'
import { Typography } from '@festina-life/ui/structure'
import { useLocation, useNavigate } from 'react-router'

import { DetailsLayout } from '../../components/detailsLayout/DetailsLayout'
import { DocumentationEntry } from '../../components/documentationEntry/DocumentationEntry'
import { EditableFormInput } from '../../components/editableFormInput/EditableFormInput'
import { EditableMarkdownViewer } from '../../components/markdownEditor/EditableMarkdownViewer'
import { OperationApproval } from '../../components/operationApproval/OperationApproval'
import { useEnumId } from '../../hooks/useEnumId'
import { useIsInlineEditingAvailable } from '../../hooks/useIsInlineEditingAvailable'
import { useTranslateLayer } from '../../hooks/useTranslateLayer'
import { configurationItem, enumLiteralPath } from '../../system/paths'
import { useNavigationParams } from '../../system/useNavigationParams'
import type { EnumLiteral } from '../../uiModels/enumLiterals/types'
import { useOperationsContext } from '../../workspace/operationsContext'
import { EnumLiteralTransferButton } from './EnumLiteralTransferButton'
import { workspaceOperationFromEnumLiteral } from './helpers'

type Props = {
  value: EnumLiteral
  onClose: () => void
}

export const EnumLiteralDetails = ({ value, onClose }: Props) => {
  const enumId = useEnumId()
  const translate = useTranslate()
  const { workspaceKey, onAddOrUpdateOperation } = useOperationsContext()
  const translateLayer = useTranslateLayer()
  const isEditable = useIsInlineEditingAvailable()
  const navigate = useNavigate()
  const {
    server,
    layer: currentLayer,
    block: currentBlock,
  } = useNavigationParams()
  const { search } = useLocation()
  const isEnumLiteralApprovalAllowed =
    value.type === 'configured' && !value.isDraft
  const saveToLayer = value.type === 'modeled' ? currentLayer : value.layer
  const saveToBlock = value.type === 'modeled' ? currentBlock : value.block

  const handleToggleDeprecated = async (nextDeprecatedState: boolean) => {
    const nextValue = workspaceOperationFromEnumLiteral(value)

    assertNotEmpty(nextValue.entity)
    nextValue.entity.deprecated = nextDeprecatedState

    await saveAndNavigateToEnumDefinition(nextValue)
  }

  const handleSaveDescription = async (nextDescription: Optional<string>) => {
    const nextValue = workspaceOperationFromEnumLiteral(value)

    assertNotEmpty(nextValue.entity)
    nextValue.entity.description = nextDescription

    await saveAndNavigateToEnumDefinition(nextValue)
  }

  const handleSaveOrdinal = async (nextOrdinal: Optional<number>) => {
    const nextValue = workspaceOperationFromEnumLiteral(value)

    assertNotEmpty(nextValue.entity)
    nextValue.entity.ordinal = nextOrdinal

    await saveAndNavigateToEnumDefinition(nextValue)
  }

  const saveAndNavigateToEnumDefinition = async (
    nextValue: EnumLiteralCreateOperation,
  ) => {
    await onAddOrUpdateOperation({
      operation: nextValue,
      layer: saveToLayer,
      block: saveToBlock,
      shouldOpenUnifiedBranchWorkspace: true,
    })

    if (!saveToLayer.equals(currentLayer)) {
      await navigate(
        `${configurationItem({
          type: 'enumDefinition',
          workspaceName: workspaceKey?.name,
          server,
          itemId: enumId,
          layer: saveToLayer,
          block: currentBlock,
          branch: workspaceKey?.branchKey.name,
        })}${search}#${enumLiteralPath({ enumName: enumId, literalName: nextValue.entity?.name ?? '' })}`,
      )
    }
  }

  const isViewingLiteralFromDifferentLayer =
    value.type === 'configured' && !value.layer.equals(currentLayer)

  return (
    <div className="flex size-full flex-col">
      <div className="flex items-center justify-between gap-x-2">
        <Typography size="lg" weight="semibold" className="p-2">
          {`${enumId}.${value.type === 'modeled' ? value.literalName : (value.internalOperation.entity?.name ?? '')}`}
        </Typography>
        <div className="flex items-center gap-x-2">
          {isEnumLiteralApprovalAllowed ? (
            <OperationApproval
              operationKey={value.internalOperation?.operationKey}
              changesetKey={value.changesetHistory?.changesetKey}
              actions={value.actions}
            />
          ) : null}
          {value.type === 'configured' ? (
            <EnumLiteralTransferButton literal={value} />
          ) : null}
          <IconButton
            onClick={onClose}
            iconSize="md"
            tip={translate('close')}
            iconName="XCircle"
          />
        </div>
      </div>
      <div className="p-2">
        {isViewingLiteralFromDifferentLayer && !!workspaceKey && (
          <Alert severity="warning">
            {translate('enumViewer.details.literal.differentLayer', {
              sourceLayer: translateLayer(value.layer),
              editInLayer: translateLayer(value.layer),
              targetLayer: translateLayer(currentLayer),
            })}
          </Alert>
        )}
        {value.type === 'configured' && value.errors.length ? (
          <>
            {value.errors.map(({ error }) => (
              <Alert key={error.errorMessage} severity="error">
                {error.errorMessage}
              </Alert>
            ))}
          </>
        ) : null}
      </div>
      <DetailsLayout>
        <DocumentationEntry
          title={translate('enumViewer.details.literal.type')}
          value={
            value.type === 'modeled'
              ? translate('enumViewer.enumType.modeled')
              : translate('enumViewer.enumType.configured')
          }
        />
        <DocumentationEntry
          title={translate('enumViewer.details.literal.description')}
          value={
            <EditableMarkdownViewer
              value={
                value.type === 'configured'
                  ? value.internalOperation.entity?.description
                  : undefined
              }
              onSave={isEditable ? handleSaveDescription : undefined}
            />
          }
        />
        <DocumentationEntry
          title={translate('enumViewer.details.literal.ordinal')}
          value={
            <EditableFormInput
              FormInput={RawDoubleInput}
              inputProps={{
                value:
                  value.type === 'configured'
                    ? value.internalOperation.entity?.ordinal
                    : undefined,
              }}
              onSave={isEditable ? handleSaveOrdinal : undefined}
            />
          }
        />
        <DocumentationEntry
          title={translate('enumViewer.details.literal.deprecated')}
          value={
            <RawCheckbox
              checked={
                value.type === 'configured' &&
                !!value.internalOperation.entity?.deprecated
              }
              onChange={handleToggleDeprecated}
              disabled={!isEditable}
            />
          }
        />
      </DetailsLayout>
    </div>
  )
}
